import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Package, ArrowUpRight, Twitter, Send, Mail } from "lucide-react";
import { getCheckoutStatus, fetchSocials } from "@/lib/api";
import { useApp } from "@/context/AppContext";

/**
 * Stripe Checkout only redirects to success_url AFTER the payment has been
 * captured successfully. The presence of ?session_id=... on this page is, by
 * itself, proof that the customer's payment went through.
 *
 * We therefore show the confirmation UI IMMEDIATELY. The background lookup to
 * /api/checkout/status/{session_id} is a NICE-TO-HAVE, it populates the
 * human-readable order number and clears the cart. If the lookup fails (502,
 * network issue, etc.), we still show "Order confirmed" with a softer fallback,
 * because the order itself is fine, the Stripe webhook records it server-to-
 * server independent of the customer's browser.
 *
 * NEVER scare a paying customer with a red "Unable to confirm payment" error.
 */
export default function Success() {
    const [params] = useSearchParams();
    // NowPayments (current provider) redirects with ?order_id=...
    // Legacy Stripe redirects had ?session_id=... — support both so no one lands on a blank page.
    const sessionId = params.get("session_id");
    const orderIdParam = params.get("order_id");
    const referenceId = sessionId || orderIdParam;
    const { clearCart, setFirstOrderHint } = useApp();
    const [order, setOrder] = useState(null);
    const [socials, setSocials] = useState({});
    const cleared = useRef(false);
    const attempts = useRef(0);
    const cancelled = useRef(false);

    useEffect(() => { fetchSocials().then(setSocials).catch(() => {}); }, []);

    // Clear cart immediately, payment succeeded the moment the provider redirected here.
    useEffect(() => {
        if (cleared.current || !referenceId) return;
        cleared.current = true;
        clearCart();
        setFirstOrderHint(false);
    }, [referenceId, clearCart, setFirstOrderHint]);

    // Background-fetch the order details. Silent on failure, we never block or
    // panic the customer based on this. Retries gently up to 4× over ~20s in
    // case the Stripe webhook hasn't finalized the DB record yet.
    useEffect(() => {
        if (!sessionId) return;
        cancelled.current = false;
        const poll = async () => {
            if (cancelled.current) return;
            attempts.current += 1;
            try {
                const res = await getCheckoutStatus(sessionId);
                if (cancelled.current) return;
                setOrder(res);
                if (res?.payment_status !== "paid" && attempts.current < 4) {
                    setTimeout(poll, 5000);
                }
            } catch (e) {
                // Silent retry, the order is fine, our status endpoint just
                // couldn't fetch the live Stripe object. Webhook will update DB.
                if (attempts.current < 4) setTimeout(poll, 5000);
            }
        };
        poll();
        return () => { cancelled.current = true; };
    }, [sessionId]);

    // If somehow someone lands on /success WITHOUT a session_id or order_id, they probably
    // navigated here manually. Send them back to the shop quietly, no scare.
    if (!referenceId) {
        return (
            <div className="bg-ink-base text-ink min-h-screen">
                <div className="max-w-2xl mx-auto px-6 py-24 text-center">
                    <p className="text-ink-dim">Looking for your shop?</p>
                    <Link to="/shop" className="mt-6 inline-flex items-center gap-2 bg-neon hover:bg-neon-glow text-black px-8 py-4 font-bold transition-colors">
                        BROWSE PRODUCTS <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-ink-base text-ink min-h-screen">
            <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="success-page">
                <div data-testid="status-paid">
                    <CheckCircle2 className="w-14 h-14 mx-auto text-neon" strokeWidth={1.3} />
                    <p className="overline text-neon mt-6">PAYMENT RECEIVED</p>
                    <h1 className="mt-3 font-display text-5xl font-bold text-ink tracking-tight">
                        Order<br /><span className="text-neon">confirmed.</span>
                    </h1>
                    <p className="mt-6 text-ink-dim max-w-md mx-auto">
                        Thank you for your research order. A confirmation will be sent to your email shortly.
                    </p>
                    {order?.order_id && (
                        <p className="mt-4 font-mono text-xs text-ink-mute uppercase tracking-wider" data-testid="order-id">
                            ORDER #{order.order_id.slice(0, 8).toUpperCase()}
                        </p>
                    )}

                    <div className="mt-10 p-5 border border-ink-strong bg-ink-surface text-left max-w-md mx-auto">
                        <div className="flex items-center gap-3">
                            <Package className="w-5 h-5 text-neon shrink-0" />
                            <div>
                                <p className="overline text-neon">DISCREET SHIPPING ACTIVATED</p>
                                <p className="text-xs text-ink-dim mt-1">Your order ships in plain unmarked packaging from our Canadian warehouse.</p>
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 p-5 border border-ink-strong bg-ink-surface text-left max-w-md mx-auto" data-testid="email-reassurance">
                        <div className="flex items-start gap-3">
                            <Mail className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                            <div>
                                <p className="overline text-gold">CONFIRMATION EMAIL</p>
                                <p className="text-xs text-ink-dim mt-1 leading-relaxed">
                                    Check your inbox in the next few minutes. If you don&apos;t see it within 30 minutes,
                                    look in spam or contact <a href="mailto:orders@helixorapeptides.com" className="text-neon hover:underline">orders@helixorapeptides.com</a> with your card statement reference.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Prominent Telegram support card — fastest way to reach us after ordering */}
                    <a
                        href={socials.telegram || "https://t.me/HelixoraSupportBot"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-6 group block max-w-md mx-auto p-5 border border-neon/40 bg-neon/5 hover:bg-neon/10 hover:border-neon transition-colors text-left"
                        data-testid="success-telegram-support"
                    >
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-neon/15 flex items-center justify-center shrink-0 group-hover:bg-neon/25 transition-colors">
                                <Send className="w-5 h-5 text-neon" strokeWidth={2} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="overline text-neon text-[10px]">QUESTIONS ABOUT YOUR ORDER?</p>
                                <p className="mt-1 text-sm text-ink font-semibold leading-snug">Message us on Telegram → @HelixoraSupportBot</p>
                                <p className="mt-1 text-xs text-ink-dim leading-relaxed">
                                    Direct line to our team for tracking, COAs, reconstitution guidance, or anything else. Most replies in under an hour.
                                </p>
                            </div>
                            <ArrowUpRight className="w-4 h-4 text-neon shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </div>
                    </a>

                    {(socials.twitter || socials.telegram) && (
                        <div className="mt-8 p-6 border border-gold/40 bg-gold/5 max-w-md mx-auto" data-testid="success-social-cta">
                            <p className="overline text-gold text-[10px] mb-2">/ STAY IN THE LOOP</p>
                            <h3 className="font-display text-2xl font-bold text-ink leading-tight">
                                Follow us for restock alerts<br /><span className="text-gold">+ flash sales.</span>
                            </h3>
                            <p className="mt-3 text-xs text-ink-dim leading-relaxed">
                                New compounds, COA drops, and members-only discounts go to our followers first. Takes 2 seconds.
                            </p>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {socials.twitter && (
                                    <a href={socials.twitter} target="_blank" rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 bg-ink hover:bg-ink-strong text-ink px-4 py-2.5 text-xs font-mono uppercase tracking-wider transition-colors"
                                        data-testid="success-follow-x">
                                        <Twitter className="w-3.5 h-3.5" /> Follow @HPeptides
                                    </a>
                                )}
                                {socials.telegram && (
                                    <a href={socials.telegram} target="_blank" rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 border border-ink-strong hover:border-neon text-ink-dim hover:text-neon px-4 py-2.5 text-xs font-mono uppercase tracking-wider transition-colors"
                                        data-testid="success-follow-tg">
                                        <Send className="w-3.5 h-3.5" /> Telegram
                                    </a>
                                )}
                            </div>
                        </div>
                    )}

                    <Link to="/shop" className="mt-10 inline-flex items-center gap-2 bg-neon hover:bg-neon-glow text-black px-8 py-4 font-bold transition-colors" data-testid="continue-shopping">
                        CONTINUE SHOPPING <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </div>
    );
}
